import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BASE_URL from "../config";
import Loader from "../components/Loader";

function BulkTaskAdder() {
  const [tasksInput, setTasksInput] = useState("");
  const [project, setProject] = useState("");
  const [projectOptions, setProjectOptions] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [dueDate, setDueDate] = useState("");
  const [createdTasks, setCreatedTasks] = useState([]); // keep for internal use, but will not display
  const [taskTitle, setTaskTitle] = useState(""); // New: Task Title
  const [listType, setListType] = useState("");   // New: List Type
  const [loading, setLoading] = useState(false);
  const LIST_TYPES = ["ON HOLD", "TODO", "DOING", "TO TEST", "DONE", "FINISHED"];

  // Fetch projects and assignees for dropdowns
  useEffect(() => {
    const fetchProjectsAndAssignees = async () => {
      try {
        const token = localStorage.getItem("token");
        // Fetch projects
        const projectsRes = await fetch(`${BASE_URL}api/project/find-all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const projectsData = await projectsRes.json();
        setProjectOptions(projectsData || []);
        // Fetch assignees (active users)
        const usersRes = await fetch(`${BASE_URL}api/user/active-users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const usersData = await usersRes.json();
        setAssignees(usersData || []);
      } catch (err) {
        setProjectOptions([]);
        setAssignees([]);
      }
    };
    fetchProjectsAndAssignees();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    const titleArr = taskTitle
      ? taskTitle.split("~~").map(t => t.trim())
      : [];
    const detailsArr = tasksInput
      .split("~~")
      .map(t => t.trim())
      .filter(Boolean);

    // Make tasks based on the maximum length of titleArr or detailsArr
    const maxLen = Math.max(titleArr.length, detailsArr.length);
    const newTasks = Array.from({ length: maxLen }).map((_, idx) => ({
      title:
        titleArr[idx] && titleArr[idx].length > 0
          ? titleArr[idx]
          : (detailsArr[idx] && detailsArr[idx].length > 0
              ? detailsArr[idx].substring(0, 30) + (detailsArr[idx].length > 30 ? "..." : "")
              : ""),
      details: detailsArr[idx] || "",
      assignee,
      date: dueDate,
      project,
      list: listType,
      color: "#0d6efd",
      img: "https://via.placeholder.com/30",
      textClass: "text-primary"
    }));

    // Console log the payload before sending to API
    console.log("BulkTaskAdder payload:", newTasks);

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}api/tasks/bulk-create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(newTasks)
      });
      if (!res.ok) {
        const err = await res.json();
        setLoading(false);
        toast.error("Error: " + (err.error || "Failed to create tasks"));
        return;
      }
      // Clear form after success
      setTasksInput("");
      setTaskTitle("");
      setListType("");
      setProject("");
      setAssignee("");
      setDueDate("");
      setLoading(false);
      toast.success("Tasks created successfully!");
    } catch (err) {
      setLoading(false);
      toast.error("Error: " + err.message);
    }
  }

  // Use full width and site-compatible style
  const containerStyle = {
    width: "100%",
    background: "#f8f9fa",
    minHeight: "calc(100vh - 120px)",
    padding: "32px 0"
  };
  const cardStyle = {
    width: "100%",
    maxWidth: "100%",
    background: "#fff",
    borderRadius: 10,
    boxShadow: "0 2px 12px rgba(34,185,142,0.08)",
    margin: "0 auto",
    padding: "32px 24px",
    border: "1px solid #e0e0e0"
  };
  const headerStyle = {
    color: "#22b98e",
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 28,
    letterSpacing: 1
  };
  const labelStyle = {
    fontWeight: 600,
    marginBottom: 6,
    display: "block",
    color: "#495057"
  };
  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    marginBottom: 18,
    borderRadius: 6,
    border: "1px solid #ced4da",
    fontSize: 16,
    background: "#f9f9f9"
  };
  const buttonStyle = {
    background: "linear-gradient(90deg, #22b98e 60%, #1e90ff 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "12px 0",
    width: "100%",
    fontWeight: 700,
    fontSize: 18,
    cursor: "pointer",
    marginTop: 10,
    boxShadow: "0 2px 8px rgba(34,185,142,0.10)"
  };
  const listStyle = {
    background: "#f7fafc",
    borderRadius: 8,
    padding: 18,
    marginTop: 18,
    boxShadow: "0 2px 8px rgba(34,185,142,0.06)"
  };
  const taskItemStyle = {
    padding: "10px 0",
    borderBottom: "1px solid #e0e0e0",
    fontSize: 16
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader message="Adding tasks..." />}
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={headerStyle}>Bulk Task Adder</div>
          <form onSubmit={handleSubmit} autoComplete="off">
            <div>
              <label style={labelStyle}>Task Title (optional, applies to all):</label>
              <input
                type="text"
                style={inputStyle}
                value={taskTitle}
                onChange={e => setTaskTitle(e.target.value)}
                placeholder="Task Title"
              />
            </div>
            <div>
              <label style={labelStyle}>List Type:</label>
              <select
                style={inputStyle}
                value={listType}
                onChange={e => setListType(e.target.value)}
                required
              >
                <option value="">Select List Type</option>
                {LIST_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                Task Details (separate with <b>~~</b>):
              </label>
              <textarea
                rows={3}
                style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
                value={tasksInput}
                onChange={e => setTasksInput(e.target.value)}
                placeholder="Task detail 1~~Task detail 2"
                required
              />
            </div>
            <div>
              <label style={labelStyle}>Project:</label>
              <select
                style={inputStyle}
                value={project}
                onChange={e => setProject(e.target.value)}
                required
              >
                <option value="">Select Project</option>
                {projectOptions.map(p => (
                  <option key={p.id} value={p.id}>{p.project_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Assignee:</label>
              <select
                style={inputStyle}
                value={assignee}
                onChange={e => setAssignee(e.target.value)}
                required
              >
                <option value="">Select Assignee</option>
                {assignees.map(u => (
                  <option key={u.id} value={u.id}>{u.username}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Due Date:</label>
              <input
                type="date"
                style={inputStyle}
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                required
              />
            </div>
            <button type="submit" style={buttonStyle}>
              Add Tasks
            </button>
          </form>
        </div>
      </div>
    </>
  );
}

export default BulkTaskAdder;